import React from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import type {
  ComponentSize,
  ComponentVariant,
  ComponentAppearance,
} from 'src/modules/shared/types/component.type';
import Button from '../../../atoms/buttons/button';
import Dropdown from '../dropdown';
import type { DropdownItem, DropdownPlacement } from '../dropdown';
import Loading from '../../../atoms/loading';
import { Text } from '../../../atoms/text';
import { getControlTextSize } from '../../../atoms/text/text.styles';

interface MenuDropdownProps {
  items: DropdownItem[];
  className?: string;
  handlerContainerClassName?: string;
  size?: ComponentSize;
  variant?: ComponentVariant;
  appearance?: ComponentAppearance;
  icon?: React.ReactNode;
  placement?: DropdownPlacement;
  label?: string | React.ReactNode;
  handlerComponent?: React.ReactNode;
  isLoading?: boolean;
  disabled?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  mobileTitle?: React.ReactNode;
}

const MenuDropdown: React.FC<MenuDropdownProps> = ({
  label,
  items,
  className = '',
  handlerContainerClassName = '',
  size = 'md',
  variant = 'primary',
  appearance = 'filled',
  icon,
  placement = 'bottom-end',
  handlerComponent,
  isLoading = false,
  disabled = false,
  onOpenChange,
  mobileTitle,
}) => {
  const defaultHandler = (
    <Button
      type="button"
      variant={variant}
      size={size}
      appearance={appearance}
      className={`!pr-2 ${className}`}
      disabled={disabled}
    >
      {icon ? <span className="flex-shrink-0">{icon}</span> : null}
      <span className="min-w-0 flex-1 text-left">
        {typeof label === 'string' ? (
          <Text
            size={getControlTextSize(size)}
            weight="regular"
            className="whitespace-nowrap truncate"
          >
            {label}
          </Text>
        ) : (
          label ?? null
        )}
      </span>
      {isLoading ? (
        <Loading size="2xs" variant={variant} />
      ) : (
        <ChevronDownIcon className="h-4 w-4 flex-shrink-0" />
      )}
    </Button>
  );

  return (
    <Dropdown
      items={items}
      handler={handlerComponent ?? defaultHandler}
      className={handlerContainerClassName}
      size={size}
      placement={placement}
      isLoading={isLoading}
      disabled={disabled}
      onOpenChange={onOpenChange}
      mobileTitle={mobileTitle}
    />
  );
};

export default MenuDropdown;
